import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiPlus } from "react-icons/bi";
import oDayJs from "dayjs";

import {
  ScreenContainer,
  Text,
  Table,
  DateInput,
  ConfirmationModal,
  Button,
  ComboBox,
} from "components";

import { BUTTON_CONFIG } from "config/constants/Button";
import {
  actionChecksChangeDateTo,
  actionChecksChangeDateFrom,
  actionChecksChangePage,
  actionChecksChangeType,
  actionChecksCleanFilters,
  actionChecksGetAll,
  selectChecksState,
  actionChecksChangeDateType,
  actionChecksDelete,
  actionChecksFind,
} from "./redux";

import { CastDate, CastFloat } from "utils/SharedUtils";
import { CHECK_DATE_TYPES_VALUES, CHECK_TYPES_VALUES } from "./constants/Checks";
import { FaTrashAlt } from "react-icons/fa";
import { CONSTANTS_COLORS } from "config/constants/Colors";

const ChecksList = () => {
  const oDispatch = useDispatch();
  const oNavigator = useNavigate();

  const [oDelete, setDelete] = useState(null);

  const {
    aChecks,
    nPage,
    nPages,
    nRecords,
    dDateTo,
    dDateFrom,
    oType,
    oDateType,
  } = useSelector(selectChecksState);

  useEffect(() => {
    oDispatch(actionChecksGetAll());
    onChangeType(CHECK_TYPES_VALUES[0]);
    onChangeDateType(CHECK_DATE_TYPES_VALUES[0]);
    return () => {
      oDispatch(actionChecksCleanFilters());
    };
  }, []);

  const onChangePage = (nSelectedPage) => {
    oDispatch(actionChecksChangePage(nSelectedPage));
    oDispatch(actionChecksGetAll());
  };

  const onChangeDateFrom = (dDate) => {
    oDispatch(actionChecksChangeDateFrom(oDayJs(dDate).toString()));
    oDispatch(actionChecksChangePage(1));
    oDispatch(actionChecksGetAll());
  };

  const onChangeDateTo = (dDate) => {
    oDispatch(actionChecksChangeDateTo(oDayJs(dDate).toString()));
    oDispatch(actionChecksChangePage(1));
    oDispatch(actionChecksGetAll());
  };

  const onChangeType = (oType) => {
    oDispatch(actionChecksChangeType(oType));
    oDispatch(actionChecksChangePage(1));
    oDispatch(actionChecksGetAll());
  };

  const onChangeDateType = (oType) => {
    oDispatch(actionChecksChangeDateType(oType));
    oDispatch(actionChecksChangePage(1));
    oDispatch(actionChecksGetAll());
  };

  const onView = (oItem) => {
    oDispatch(actionChecksFind(oItem));
    oNavigator(`/checks/${oItem.id}`);
  };

  const onDelete = (oItem) => {
    if (!oDelete) return setDelete(oItem);
    oDispatch(actionChecksDelete(oDelete.id));
    setDelete(null);
  };

  return (
    <ScreenContainer>
      <section className="grid grid-cols-2">
        <h1 className="ui-page-title self-center">
          Cheques
        </h1>
        <div className="ml-auto gap-2 flex">
          <Button
            label="Nuevo"
            type={BUTTON_CONFIG.CLASS.PRIMARY}
            onClick={() => oNavigator("/checks/new")}
            icon={<BiPlus className="text-white ml-2 text-lg" />}
          />
        </div>
      </section>
      <section className="grid grid-cols-1 md:grid-cols-3 mt-4 gap-5">
        <div className="relative col-span-2 md:col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Tipo de fecha</span>
          </div>
          <ComboBox
            size="sm"
            selected={oDateType}
            emptyLabel="Tipo de fecha"
            onChange={onChangeDateType}
            options={CHECK_DATE_TYPES_VALUES}
          />
        </div>
        <div className="relative gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Desde</span>
          </div>
          <DateInput
            value={dDateFrom}
            onChange={onChangeDateFrom}
            placeholder="Fecha desde"
            containerClassName="rounded-lg overflow-hidden ml-12"
            className="ui-field w-full"
          />
        </div>
        <div className="relative gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Hasta</span>
          </div>
          <DateInput
            value={dDateTo}
            onChange={onChangeDateTo}
            placeholder="Fecha hasta"
            containerClassName="rounded-lg overflow-hidden ml-12"
            className="ui-field w-full"
          />
        </div>
        <div className="relative col-span-2 md:col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Tipo de cheque</span>
          </div>
          <ComboBox
            size="sm"
            selected={oType}
            onChange={onChangeType}
            emptyLabel="Tipo de cheque"
            options={CHECK_TYPES_VALUES}
          />
        </div>
      </section>
      <section className="py-5">
        <Table>
          <Table.Header>
            <Table.Row isHeaderOrFooter>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Serie
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center hidden md:table-cell">
                Ingreso
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center hidden md:table-cell">
                Vencimiento
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2">
                Tipo
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2">
                Banco
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right hidden md:table-cell">
                Moneda / Importe
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Socio
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center hidden md:table-cell">
                Observaciones
              </Table.Col>
              <Table.Col scope="col" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {aChecks?.length > 0 ? (
              aChecks.map((oItem, index) => (
                <Table.Row key={`${oItem.id}_${index}`} onClick={() => onView(oItem)}>
                  <Table.Col scope="row" className="text-xs md:text-sm px-3 py-2 font-medium text-gray-800 dark:text-white whitespace-nowrap text-center">
                    {oItem.code}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center hidden md:table-cell">
                    {CastDate(oItem.date)}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center hidden md:table-cell">
                    {CastDate(oItem.expiration)}
                  </Table.Col>
                  <Table.Col className="px-3 py-2">
                    {oItem.type?.label || "-"}
                  </Table.Col>
                  <Table.Col className="px-3 py-2">
                    {oItem.bank?.name || "-"}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right hidden md:table-cell">
                    {oItem?.currency?.code || "S/M"} {CastFloat(oItem.amount)}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem?.partner?.name}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center hidden md:table-cell">
                    {oItem?.observations}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 flex items-center justify-center gap-3">
                    <div className="flex justify-center">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(oItem);
                        }}
                        size={BUTTON_CONFIG.SIZE.SM}
                        type={BUTTON_CONFIG.CLASS.DANGER}
                        icon={<FaTrashAlt size={16} color={CONSTANTS_COLORS.COLORS.WHITE_COLOR} />}
                      />
                    </div>
                  </Table.Col>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Col className="px-3 py-2 text-center" colSpan={9}>
                  <em>
                    No se encontraron resultados
                  </em>
                </Table.Col>
              </Table.Row>
            )}
          </Table.Body>
          <Table.Footer>
            <Table.Row isHeaderOrFooter>
              <Table.Col colSpan="2" className="px-3 py-2 text-center md:hidden">
                <Text>Total: {nRecords || "0"}</Text>
              </Table.Col>
              <Table.Col colSpan="3" className="px-3 py-2 text-center md:hidden">
                <Table.Pagination total={nPages} page={nPage} onChange={onChangePage} records={nRecords} />
              </Table.Col>
            </Table.Row>
            <Table.Row isHeaderOrFooter>
              <Table.Col colSpan="4" className="px-3 py-2 text-center md:text-left hidden md:table-cell">
                <Text>Total: {nRecords || "0"}</Text>
              </Table.Col>
              <Table.Col colSpan="5" className="px-3 py-2 text-right hidden md:table-cell">
                <Table.Pagination total={nPages} page={nPage} onChange={onChangePage} records={nRecords} />
              </Table.Col>
            </Table.Row>
          </Table.Footer>
        </Table>
      </section>
      <ConfirmationModal
        show={!!oDelete}
        onConfirm={onDelete}
        title="Eliminar cheque"
        onClose={() => setDelete(null)}
        question={`Está seguro que desea eliminar el cheque "${oDelete?.code}"`}
      />
    </ScreenContainer>
  );
};

export default ChecksList;
